'use client'

import { Text } from '@/components/ui/text'
import { Box, Flex, type HTMLStyledProps } from '@/styled-system/jsx'
import { token } from '@/styled-system/tokens'
import { memo, useEffect, useState } from 'react'

const CARD_COLORS = {
  C: '#000',
  D: '#EF2B2D',
  H: '#EF2B2D',
  S: '#000',
}

const CARD_SUITS = {
  C: (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 30 30'
      width='1em'
      height='1em'
      fill='currentColor'
    >
      <path
        d='M14.2088 22.096C13.2312 24.9297 11.8109 27.4895 9.99488 29.7517H20.0052C18.1892 27.4895 16.7929 24.93 15.7913 22.096C17.2817 23.9471 20.1453 24.9986 22.5893 24.9986C26.6401 24.9986 29.9464 21.7764 29.9464 17.7773C29.9464 13.7781 26.6401 10.555 22.5893 10.555C22.2637 10.555 21.9611 10.5785 21.6348 10.6011C21.6578 10.5785 21.7048 10.4862 21.6348 10.6011C21.9844 9.96131 22.3568 8.49827 22.3568 7.49313C22.3568 3.49376 19.0735 0.248352 14.9999 0.248352C10.9264 0.248352 7.64285 3.49376 7.64285 7.49313C7.64285 8.61314 7.89911 9.66387 8.36486 10.6011C8.06227 10.5785 7.73664 10.555 7.41031 10.555C3.35958 10.555 0.0532227 13.7779 0.0532227 17.7773C0.0532227 21.7766 3.35958 24.9986 7.41031 24.9986C9.85431 24.9986 12.718 23.9471 14.2083 22.096H14.2088Z'
        fillRule='evenodd'
        clipRule='evenodd'
      />
    </svg>
  ),
  D: (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 30 34'
      width='1em'
      height='1em'
      fill='currentColor'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M14.9884 0.579834C10.7279 6.45296 5.81577 11.9379 0.274902 16.9887C5.816 22.0621 10.7279 27.547 14.9884 33.4202C19.2719 27.547 24.1843 22.0621 29.7249 16.9887C24.1843 11.9379 19.2719 6.45319 14.9884 0.579834Z'
      />
    </svg>
  ),
  H: (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 32 28'
      width='1em'
      height='1em'
      fill='currentColor'
    >
      <path
        d='M16.0108 6.08833C12.426 -2.39065 0.203075 0.100322 0.575722 8.60209C0.902044 15.7094 11.1689 22.2223 16.011 27.1589C20.8531 22.2223 31.1197 15.7094 31.4232 8.60209C31.8189 0.10055 19.5969 -2.39042 16.011 6.08855L16.0108 6.08833Z'
        fillRule='evenodd'
        clipRule='evenodd'
      />
    </svg>
  ),
  S: (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 30 30'
      width='1em'
      height='1em'
      fill='currentColor'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M15.0119 0.168213C8.8654 5.69855 0.414557 11.7779 0.0654142 17.6736C-0.330281 24.6207 7.95786 27.6836 13.8944 22.5642C13.0095 25.1012 11.6127 27.5236 9.75012 29.8318H20.2728C18.3876 27.5236 17.0134 25.1014 16.1059 22.5642C22.0424 27.6836 30.3301 24.621 29.9344 17.6736C29.6088 11.7777 21.1575 5.69832 15.0114 0.168213L15.0119 0.168213Z'
      />
    </svg>
  ),
}

export type PlayingCardRank =
  | 'A'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | 'J'
  | 'Q'
  | 'K'

export type PlayingCardSuit = 'C' | 'D' | 'H' | 'S'

export type PlayingCardType = {
  rank: PlayingCardRank
  suit: PlayingCardSuit
}

export type PlayingCardProps = HTMLStyledProps<'div'> & {
  card?: PlayingCardType
  disableFlip?: boolean
  faceDown?: boolean
  status?: 'active' | 'win' | 'draw' | 'lose' | 'none'
}

const PlayingCard = memo(function PlayingCard({
  card,
  faceDown,
  disableFlip,
  status = 'none',
  ...props
}: PlayingCardProps) {
  const [oldCard, setOldCard] = useState<PlayingCardType>(
    card ?? { rank: 'A', suit: 'C' }
  )
  const isFlipped = card && !faceDown

  useEffect(() => {
    if (card) {
      setOldCard(card)
    }
  }, [card])

  const Suit = CARD_SUITS[oldCard.suit]
  const color = CARD_COLORS[oldCard.suit]

  return (
    <Box
      position='relative'
      w='7.5em'
      borderRadius='0.5em'
      boxShadow='0 0.125em 0.125em rgba(0, 0, 0, 0.15)'
      userSelect='none'
      style={{
        color,
        transformStyle: 'preserve-3d',
        perspective: '700px',
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
        transition: !disableFlip ? 'transform 0.3s ease-out' : 'none',
      }}
      _before={{
        content: '""',
        display: 'block',
        paddingTop: '150%',
      }}
      {...props}
    >
      {/* Face */}
      <Box
        position='absolute'
        top={0}
        left={0}
        w='full'
        h='full'
        borderRadius='0.5em'
        bg='#fff'
        style={{
          boxShadow: {
            active: `0 0 0 0.3em ${token('colors.info')}`,
            win: `0 0 0 0.3em ${token('colors.success')}`,
            draw: `0 0 0 0.3em ${token('colors.danger')}`,
            lose: `0 0 0 0.3em ${token('colors.error')}`,
            none: 'none',
          }[status],
        }}
        transform='scaleX(-1)'
        _before={{
          content: '""',
          display: 'block',
          position: 'absolute',
          inset: '15%',
          border: '0.0625em solid currentColor',
          borderRadius: '0.25em',
        }}
      >
        <Flex
          position='absolute'
          top='calc(15% + 0.625em)'
          left='15%'
          direction='column'
          align='center'
          w='2em'
          pb='0.5em'
          bg='#fff'
          transform='translate(-50%, -50%)'
        >
          <Text as='span' fontSize='1.875em' fontWeight='bold' lineHeight={1}>
            {oldCard.rank}
          </Text>
          <Box fontSize='0.75em'>{Suit}</Box>
        </Flex>

        <Box
          position='absolute'
          top='50%'
          left='50%'
          transform='translate(-50%, -50%)'
        >
          <Box fontSize='1.875em'>{Suit}</Box>
        </Box>

        <Flex
          position='absolute'
          bottom='calc(15% + 0.625em)'
          right='15%'
          direction='column'
          align='center'
          w='2em'
          pb='0.5em'
          bg='#fff'
          transform='translate(50%, 50%) rotate(180deg)'
        >
          <Text as='span' fontSize='1.875em' fontWeight='bold' lineHeight={1}>
            {oldCard.rank}
          </Text>
          <Box fontSize='0.75em'>{Suit}</Box>
        </Flex>
      </Box>

      {/* Back */}
      <Box
        position='absolute'
        top={0}
        left={0}
        w='full'
        h='full'
        bg='neutral.200'
        boxShadow='0 0 0 0.1875em token(colors.neutral.500) inset'
        borderRadius='0.5em'
        style={{
          boxShadow: `0 0 0 0.1875em ${token('colors.neutral.500')} inset`,
          backfaceVisibility: 'hidden',
        }}
        _before={{
          content: '""',
          display: 'block',
          position: 'absolute',
          inset: '15%',
          bgGradient: 'to-b',
          gradientFrom: 'transparent',
          gradientTo: 'neutral.50',
          borderRadius: '0.25em',
          border: '0.0625em solid',
          borderColor: 'neutral.500',
        }}
      />
    </Box>
  )
})

export default PlayingCard
